import { defineCollection } from "@rebasepro/common";
import emailThreadsCollection from "./email_threads.js";
import emailsCollection from "./emails.js";
import mailboxesCollection from "./mailboxes.js";
/**
 * Email messages — what was actually said, in both directions.
 *
 * One table for sent and received, because a conversation split across two
 * tables is not a conversation. `direction` separates them and `status` says
 * what happened to each.
 *
 * ── What the statuses claim ──────────────────────────────────────────────────
 *
 *   Received — it arrived. The only status an inbound message can have.
 *   Sent     — the SMTP server accepted it. **Not** delivered and **not** read:
 *              nothing in this app can observe either, so no value names them.
 *   Failed   — SMTP refused it or the connection died. The row is written anyway
 *              and keeps its text, so the attempt is visible and the words are
 *              not lost.
 *
 * There is no `draft` here. A draft lives in `emails`, which is the review queue
 * the agent writes into and a person approves from; a message appears in this
 * table at the moment it is actually sent. A status nothing ever writes is a
 * promise the code does not keep.
 *
 * ── About the bodies ─────────────────────────────────────────────────────────
 * `body_html` is stored **already sanitized** — `backend/src/agent/inbox/
 * sanitize.ts` runs on the way in, so the dangerous form of a stranger's markup
 * is never at rest in this database. Anything rendering it should sandbox it as
 * well: one layer is how these stories start.
 *
 * Attachments are recorded by name and never stored. There is no retention
 * policy for a stranger's PDF and no reason to hold one; `attachment_names`
 * exists so a thread does not silently lose the fact that something came with a
 * message.
 */
const emailMessagesCollection = defineCollection({
    name: "Messages",
    singularName: "Message",
    slug: "email_messages",
    table: "email_messages",
    description: "Every message in and out of the mailbox, bound to the conversation it belongs to.",
    properties: {
        id: { name: "ID", type: "string", isId: "uuid", admin: { readOnly: true } },
        thread: {
            name: "Conversation",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => emailThreadsCollection,
                relationName: "thread",
                localKey: "thread_id",
                // A message without its conversation is unreadable, and the
                // conversation is the only thing that says who it is with.
                onDelete: "cascade"
            },
            validation: { required: true }
        },
        email: {
            name: "From the draft",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => emailsCollection,
                relationName: "email",
                localKey: "email_id",
                onDelete: "set null"
            },
            description: "The reviewed draft this send came from, when it came from one. Empty for a reply typed in the inbox."
        },
        mailbox: {
            name: "Mailbox",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => mailboxesCollection,
                relationName: "mailbox",
                localKey: "mailbox_id",
                onDelete: "set null"
            },
            description: "The inbox this copy was read from, or sent through"
        },
        direction: {
            name: "Direction",
            type: "string",
            validation: { required: true },
            enum: [
                { id: "inbound", label: "Received", color: "green" },
                { id: "outbound", label: "Sent", color: "blue" }
            ]
        },
        status: {
            name: "Status",
            type: "string",
            validation: { required: true },
            enum: [
                { id: "received", label: "Received", color: "green" },
                { id: "sent", label: "Sent", color: "blue" },
                { id: "failed", label: "Failed", color: "red" }
                // No "delivered" and no "read": nothing here can know either.
            ]
        },
        message_id: {
            name: "Message-ID",
            type: "string",
            validation: { unique: true },
            description: "The message's own header id. Unique, so re-reading a mailbox cannot duplicate a conversation."
        },
        in_reply_to: { name: "In-Reply-To", type: "string" },
        refs: {
            name: "References",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Message-ID" },
            description: "The chain this message descends from, oldest first. What makes threading exact rather than a guess.",
            admin: { readOnly: true }
        },
        from_email: { name: "From", type: "string", validation: { required: true } },
        from_name: { name: "From name", type: "string" },
        to_emails: {
            name: "To",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Address" }
        },
        cc_emails: {
            name: "Cc",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Address" }
        },
        subject: { name: "Subject", type: "string" },
        body_text: {
            name: "Text",
            type: "string",
            admin: { multiline: true },
            description: "Always present, even for a message that arrived as HTML only — the preview and any search read it."
        },
        body_html: {
            name: "HTML",
            type: "string",
            admin: { multiline: true, readOnly: true },
            description: "Sanitized on the way in. Render it sandboxed anyway."
        },
        body_snippet: {
            name: "Preview",
            type: "string",
            description: "The first words the author wrote this time, with the quoted history removed",
            admin: { readOnly: true }
        },
        has_attachments: { name: "Has attachments", type: "boolean", defaultValue: false },
        attachment_names: {
            name: "Attachments",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "File name" },
            description: "Recorded by name and never stored. There is no retention policy for a stranger's file."
        },
        automated_signals: {
            name: "Machine signals",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Signal" },
            description: "The headers that said a machine wrote this (List-Unsubscribe, Auto-Submitted, an empty Return-Path). Kept so the filing can be explained rather than just trusted.",
            admin: { readOnly: true }
        },
        sent_at: { name: "Sent at", type: "date", validation: { required: true } },
        actor: {
            name: "Sent by",
            type: "string",
            description: "Who sent it, for an outbound message. Empty for anything that arrived."
        },
        error: { name: "Error", type: "string", admin: { readOnly: true, multiline: true } },
        imap_mailbox: { name: "Folder", type: "string", admin: { readOnly: true } },
        imap_uid: { name: "IMAP UID", type: "number", admin: { readOnly: true } },
        gmail_thread_id: { name: "Gmail conversation", type: "string", admin: { readOnly: true } },
        created_at: { name: "Stored", type: "date", autoValue: "on_create", admin: { readOnly: true } }
    },
    admin: {
        icon: "Mail",
        group: "Outreach",
        defaultViewMode: "list",
        enabledViews: ["list", "table"],
        display: {
            title: ({ entity }) => {
                const name = typeof entity.values.from_name === "string" ? entity.values.from_name.trim() : "";
                return name || String(entity.values.from_email ?? "");
            },
            subtitle: ({ entity }) => [entity.values.subject, entity.values.body_snippet]
                .map(value => (typeof value === "string" ? value.trim() : ""))
                .filter(Boolean)
                .join(" — "),
            status: "status",
            date: "sent_at",
            tags: ({ entity }) => [
                entity.values.direction === "outbound" ? "sent" : "received",
                entity.values.has_attachments ? "attachment" : ""
            ].filter(Boolean)
        },
        sort: ["sent_at", "desc"],
        filterPresets: [
            { label: "Received", filterValues: { direction: ["==", "inbound"] } },
            { label: "Sent", filterValues: { direction: ["==", "outbound"] } },
            { label: "Failed to send", filterValues: { status: ["==", "failed"] } }
        ],
        propertiesOrder: [
            "sent_at",
            "mailbox",
            "direction",
            "from_email",
            "from_name",
            "subject",
            "body_snippet",
            "thread",
            "to_emails",
            "cc_emails",
            "status",
            "has_attachments",
            "attachment_names",
            "body_text",
            "body_html",
            "email",
            "actor",
            "error",
            "automated_signals",
            "message_id",
            "in_reply_to",
            "refs",
            "imap_mailbox",
            "imap_uid",
            "gmail_thread_id",
            "created_at"
        ]
    }
});
export default emailMessagesCollection;
